// The battle stage: the board as a small arena in the hunt's pixel art, for setting a team up and for watching a fight.
//
// Top-down like the hunt: a patch of the region's ground (client/arenascene.ts) with trees along the far edge, your
// side on the left and theirs on the right, each with a back and a front column of five cells (the board's two rows,
// shared/board.ts). Creatures stand on their cells with their rig sheets (idle, walk, attack) at the hunt's scale, and
// the trainers stand behind their teams.
//
// - Setup (boardPrep): drag a creature of yours to another cell, or tap it and then a cell. A taken cell swaps the two;
//   the front column holds BOARD.frontMax. Tapping any creature shows its card. The lane a creature stands in is looks
//   only (the fight reads rows); lanes are kept on the device.
// - Fight (boardView): a board result plays back from its log. The trainers face off ("versus"), then melee creatures
//   walk up to whoever they hit and swing, ranged ones cast from their cell, every move draws the hunt's move effect
//   for its kind and element (client/movefx.ts), a Signature or Awakened move slows time, shakes the ground and names
//   itself. A fighting-game bar along the top shows both teams' HP and the clock; numbers float off every blow (bigger
//   on a crit), knocked-out creatures fade, the winners cheer. Play, pause, speed, skip, a damage meter and a card for
//   any creature tapped. It starts by itself the first time it shows (at the end, under reduced motion).
//
// Every frame comes from the replay clock and the log, so seeking, re-renders and replays draw the same frame. The HTML
// around the canvas (controls, meter, card) is patched (client/dom.ts) a few times a second.
import type { BattleResult, Blow } from '../shared/arena.ts';
import { BOARD, reachOf, type BoardRow, type Reach } from '../shared/board.ts';
import { kitMoves } from '../shared/combat.ts';
import { MOVE, type MoveDef } from '../shared/moves.ts';
import { SPECIES } from '../shared/species.ts';
import { ELEMENTS, type Element } from '../shared/elements.ts';
import { heldName } from '../shared/held.ts';
import type { HeldItem } from '../shared/held.ts';
import { canvasDpr, frameTime, liteMode } from './perf.ts';
import { creatureIcon, sheet, drawable as ready, silhouette, splitAlpha, sheetInfo, sheetCell, sprite } from './assets.ts';
import { particleSprite, stamp } from './hitfx.ts';
import { drawMove, fxBegin, fxDuration, fxLand } from './movefx.ts';
import { arenaGround, lairGround, SCENE_BIOME, type SceneCell } from './arenascene.ts';
import { battleSynergyChips } from './synergy.ts';
import { patch } from './dom.ts';
import { esc, full, big } from './fmt.ts';

/** Backdrop: a ranked league, or 'plain' (every other mode). */
export type BoardStage = 'plain' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'mythic' | 'legend';
/** Extras a caller can give: the ground (a SCENE_BIOME key or a biome id) and each side's trainer sprite (client/trainerart.ts). */
export interface StageOpts {
  scene?: string; trainers?: [string | null, string | null];
  /** The line shown when the fight ends, instead of Victory or Defeat. */
  end?: string;
  /** A boss that can't fall (the World Boss): the top bar counts the damage dealt instead of its HP. */
  counter?: boolean;
}

// ---------------------------------------------------------------- layout

const LANES = 5;
/** Columns left to right: your back, your front, their front, their back (fractions of the width). */
const COL_X = { left: { back: 0.15, front: 0.33 }, right: { front: 0.67, back: 0.85 } } as const;
/** The arena in source pixels: wide (16:9) or, on a narrow screen, taller than wide. */
interface Dims { aw: number; ah: number; K: number; top: number; gap: number; narrow: boolean }
/**
 * Each board canvas's CSS width, kept by a ResizeObserver. Reading clientWidth every frame forced a page layout right
 * after the HTML round (patch) changed the page: a stall each frame of a battle.
 */
const widths = new WeakMap<HTMLCanvasElement, number>();
const sizer = typeof ResizeObserver === 'function' ? new ResizeObserver((es) => { for (const e of es) widths.set(e.target as HTMLCanvasElement, (e.target as HTMLCanvasElement).clientWidth); }) : null;
/** A board canvas's CSS width (clientWidth), without forcing a layout after the first read. */
function cssWidth(cv: HTMLCanvasElement): number {
  let w = widths.get(cv);
  if (w === undefined) { w = cv.clientWidth; if (sizer) { widths.set(cv, w); sizer.observe(cv); } }
  return w;
}
function dimsOf(cv: HTMLCanvasElement): Dims {
  const narrow = cssWidth(cv) < 520;
  const aw = narrow ? 300 : 480, ah = narrow ? 360 : 270;
  return { aw, ah, K: cv.width / aw, top: narrow ? 0.3 : 0.36, gap: narrow ? 0.14 : 0.135, narrow };
}
/** A board point (x fraction, lane) in source pixels. */
const src = (d: Dims, x: number, l: number) => ({ x: x * d.aw, y: d.ah * (d.top + l * d.gap) });
/** The same in canvas pixels. */
const dev = (d: Dims, x: number, l: number) => { const p = src(d, x, l); return { x: p.x * d.K, y: p.y * d.K }; };
/** A creature's sheet cell, drawn at the hunt's scale (80 px cells at 0.75). */
const UNIT = 60;
const sceneCells = (d: Dims): SceneCell[] => (['left', 'right'] as const).flatMap((side) => (['back', 'front'] as const).flatMap((row) =>
  Array.from({ length: LANES }, (_, l) => ({ ...src(d, COL_X[side][row], l), mine: side === 'left' }))));
const biomeOf = (scene?: string) => SCENE_BIOME[scene ?? 'plain'] ?? scene ?? 'ruins';

/** Each lane's preference when nothing was set: n creatures in a row stand centred. */
const SPREAD: Record<number, number[]> = { 0: [], 1: [2], 2: [1, 3], 3: [1, 2, 3], 4: [0, 1, 3, 4], 5: [0, 1, 2, 3, 4] };

const LANE_KEY = 'mi-board-lanes';
let laneStore: Record<string, number> = (() => { try { return JSON.parse(localStorage.getItem(LANE_KEY) ?? '{}') ?? {}; } catch { return {}; } })();
function saveLanes() { try { localStorage.setItem(LANE_KEY, JSON.stringify(laneStore)); } catch { /* storage off */ } }

/** Lanes for one side: each creature's kept lane when it is free in its row, the rest centred in the lanes left. */
function resolveLanes(units: readonly { id: number; row: BoardRow }[], keep: boolean): number[] {
  const out = units.map(() => -1);
  for (const row of ['front', 'back'] as const) {
    const idx = units.map((u, i) => (u.row === row ? i : -1)).filter((i) => i >= 0);
    const taken = new Set<number>();
    if (keep) for (const i of idx) {
      const l = laneStore[String(units[i].id)];
      if (units[i].id > 0 && Number.isInteger(l) && l >= 0 && l < LANES && !taken.has(l)) { out[i] = l; taken.add(l); }
    }
    const rest = idx.filter((i) => out[i] < 0);
    const want = SPREAD[Math.min(LANES, idx.length)] ?? [];
    const free = [...want.filter((l) => !taken.has(l)), ...[2, 1, 3, 0, 4].filter((l) => !taken.has(l) && !want.includes(l))];
    rest.forEach((i, k) => { out[i] = free[k] ?? 2; taken.add(out[i]); });
  }
  return out;
}

// ---------------------------------------------------------------- drawing

const C = {
  ink: '#f7ecd2', shadow: '#120b07', gold: '#f4c86a', bad: '#ea5a45', good: '#8be08f',
  hpMine: '#5fd16a', hpTheirs: '#ec5b45', ghost: '#fff1c8', charge: '#5fb0ff', chargeFull: '#b9e0ff',
};
const elColor = (el?: Element) => (el && ELEMENTS[el]?.color) || C.gold;
const FONT = '"Cormorant Garamond", Georgia, serif';
const SANS = 'system-ui, -apple-system, "Segoe UI", sans-serif';

type Face = 'front' | 'left' | 'back' | 'right';
const ROWS_OF: Record<Face, number> = { front: 0, left: 1, back: 2, right: 3 };
const ATK_MS = 70, WALK_MS = 130, IDLE_MS = 150;

interface Look { alpha?: number; flash?: number; tint?: string; lift?: number; scale?: number }
/** One creature or trainer, feet at (x, y) in canvas pixels, K canvas pixels a source pixel. */
function drawUnit(g: CanvasRenderingContext2D, id: string, aura: boolean, x: number, y: number, K: number, face: Face, anim: 'idle' | 'walk' | 'atk', ms: number, seed: number, look: Look = {}) {
  const info = sheetInfo(id);
  const size = UNIT * K * (look.scale ?? 1);
  // No save()/restore() here: on a canvas drawn without a GPU, restore() was the costliest call on the board.
  const was = g.globalAlpha;
  g.globalAlpha = look.alpha ?? 1;
  // Shadow, in whole source pixels.
  g.fillStyle = 'rgba(0,0,0,.3)';
  g.beginPath(); g.ellipse(Math.round(x), Math.round(y), 13 * K * (look.scale ?? 1), 4 * K * (look.scale ?? 1), 0, 0, Math.PI * 2); g.fill();
  const lift = look.lift ?? 0;
  if (info) {
    const img = sheet(id, aura);
    if (ready(img)) {
      const n = info.size, p = sheetCell(info);
      let col: number;
      if (anim === 'atk') col = info.walk + info.idle + Math.min(info.atk - 1, Math.max(0, Math.floor(ms / ATK_MS)));
      else if (anim === 'walk') col = Math.floor((ms + seed) / WALK_MS) % info.walk;
      else col = info.walk + (Math.floor((ms + seed) / IDLE_MS) % info.idle);
      const foot = y + size * (7 / n) - lift;
      const dx = Math.round(x - size / 2), dy = Math.round(foot - size);
      g.drawImage(img, col * p, ROWS_OF[face] * p, p, p, dx, dy, Math.round(size), Math.round(size));
      if (look.flash || look.tint) {
        // The hit flash (white) or a tint: the frame in that colour over itself.
        const [color, a] = look.flash ? ['#fff', 0.85 * look.flash] : splitAlpha(look.tint!);
        g.globalAlpha *= a;
        g.drawImage(silhouette(img, col * p, ROWS_OF[face] * p, p, color), 0, 0, p, p, dx, dy, Math.round(size), Math.round(size));
      }
    }
  } else {
    const img = sprite(id, aura);
    if (ready(img)) {
      const w = size * 0.62;
      g.save();
      g.translate(Math.round(x), Math.round(y - lift));
      if (face === 'left') g.scale(-1, 1);
      g.drawImage(img, Math.round(-w / 2), Math.round(-w), Math.round(w), Math.round(w));
      g.restore();
    }
  }
  g.globalAlpha = was;
}

/**
 * Outlined labels and numbers, each drawn once into a small canvas and stamped after. Setting a font and stroking text
 * every frame cost more than all the creatures on a phone or a computer without a GPU; a damage number lives for many
 * frames, so nearly every draw is a stamp.
 */
const texts = new Map<string, { c: HTMLCanvasElement; w: number; h: number; pad: number }>();
if (typeof document !== 'undefined') document.fonts?.addEventListener?.('loadingdone', () => texts.clear());
function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, color: string, opts: { font?: string; weight?: number; align?: CanvasTextAlign; alpha?: number; line?: number; scale?: number } = {}) {
  const font = `${opts.weight ?? 700} ${Math.round(size)}px ${opts.font ?? SANS}`;
  const line = opts.line ?? Math.max(2, size / 5);
  const align = opts.align ?? 'center';
  const alpha = Math.max(0, Math.min(1, opts.alpha ?? 1));
  // Until the web fonts are in, draw directly (a stamp would keep the fallback face).
  const sc = opts.scale ?? 1;
  if (document.fonts && document.fonts.status !== 'loaded') {
    g.save();
    if (sc !== 1) { g.translate(x, y); g.scale(sc, sc); g.translate(-x, -y); }
    g.globalAlpha = alpha; g.font = font; g.textAlign = align; g.textBaseline = 'middle'; g.lineJoin = 'round'; g.lineWidth = line;
    g.strokeStyle = C.shadow; g.strokeText(s, x, y);
    g.fillStyle = color; g.fillText(s, x, y);
    g.restore();
    return;
  }
  const key = `${font}|${line}|${color}|${s}`;
  let t = texts.get(key);
  if (!t) {
    const c = document.createElement('canvas');
    let x0 = c.getContext('2d')!;
    x0.font = font;
    const tw = x0.measureText(s).width, pad = Math.ceil(line) + 2;
    c.width = Math.max(1, Math.ceil(tw) + pad * 2); c.height = Math.ceil(size * 1.6) + pad * 2;
    x0 = c.getContext('2d')!;
    x0.font = font; x0.textAlign = 'left'; x0.textBaseline = 'middle'; x0.lineJoin = 'round'; x0.lineWidth = line;
    x0.strokeStyle = C.shadow; x0.strokeText(s, pad, c.height / 2);
    x0.fillStyle = color; x0.fillText(s, pad, c.height / 2);
    t = { c, w: tw, h: c.height, pad };
    if (texts.size >= 400) for (const k of [...texts.keys()].slice(0, 80)) texts.delete(k);
    texts.set(key, t);
  }
  const left = align === 'center' ? x - (t.w / 2) * sc : align === 'right' || align === 'end' ? x - t.w * sc : x;
  const was = g.globalAlpha;
  g.globalAlpha = alpha;
  if (sc === 1) g.drawImage(t.c, Math.round(left - t.pad), Math.round(y - t.h / 2));
  else {
    // Growing or popping text: the stamp scaled, smoothly.
    g.imageSmoothingEnabled = true;
    g.drawImage(t.c, left - t.pad * sc, y - (t.h / 2) * sc, t.c.width * sc, t.h * sc);
    g.imageSmoothingEnabled = false;
  }
  g.globalAlpha = was;
}

/** HP and charge bars over a creature, in source-pixel blocks. */
function bars(g: CanvasRenderingContext2D, x: number, y: number, K: number, hp: number, ghost: number, max: number, mine: boolean, charge: number | null, alpha = 1) {
  const w = 28 * K, h = 3 * K, X = Math.round(x - w / 2), Y = Math.round(y);
  g.save();
  g.globalAlpha = alpha;
  g.fillStyle = C.shadow;
  g.fillRect(X - K, Y - K, w + 2 * K, h + 2 * K + (charge === null ? 0 : 3 * K));
  const f = Math.max(0, Math.min(1, hp / max)), gf = Math.max(f, Math.min(1, ghost / max));
  g.fillStyle = '#3a2a22'; g.fillRect(X, Y, w, h);
  g.fillStyle = C.ghost; g.fillRect(X, Y, Math.round(w * gf), h);
  g.fillStyle = mine ? C.hpMine : C.hpTheirs; g.fillRect(X, Y, Math.round(w * f), h);
  g.fillStyle = 'rgba(255,255,255,.28)'; g.fillRect(X, Y, Math.round(w * f), K);
  if (charge !== null) {
    const c = Math.max(0, Math.min(1, charge / BOARD.charge.max));
    g.fillStyle = '#1d2a3a'; g.fillRect(X, Y + h + K, w, 2 * K);
    g.fillStyle = c >= 1 ? C.chargeFull : C.charge; g.fillRect(X, Y + h + K, Math.round(w * c), 2 * K);
  }
  g.restore();
}

function rnd(seed: number, i: number) {
  const x = Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/** A puff of element particles (knockouts). */
function puff(g: CanvasRenderingContext2D, el: Element, x: number, y: number, age: number, dur: number, n: number, reach: number, seed: number, K: number) {
  const t = age / dur;
  if (t < 0 || t > 1) return;
  g.save();
  g.globalAlpha = 1 - t;
  for (let i = 0; i < n; i++) {
    const a = rnd(seed, i) * Math.PI * 2, r = (0.3 + 0.7 * rnd(seed, i + 50)) * reach * (0.2 + 0.8 * Math.sqrt(t));
    stamp(g, particleSprite(el, i % 3 === 0), x + Math.cos(a) * r, y + Math.sin(a) * r * 0.6 - t * reach * 0.4, K);
  }
  g.restore();
}

/** Weather by biome: snow, embers, petals or motes, drifting by the wall clock. */
function weather(g: CanvasRenderingContext2D, d: Dims, biome: string, real: number) {
  const kind = /snow|taiga|fjord|glacier/.test(biome) ? 'snow' : /ember|ash|underworld/.test(biome) ? 'ember' : /shrine|bamboo|village/.test(biome) ? 'petal' : 'mote';
  const n = (kind === 'snow' ? 46 : 26) >> (liteMode() ? 1 : 0);
  const K = d.K;
  g.save();
  for (let i = 0; i < n; i++) {
    const sp = 0.6 + rnd(i, 1) * 0.8;
    const t = (real / 1000) * sp + rnd(i, 2) * 50;
    let x: number, y: number, col: string, s = 1;
    if (kind === 'ember') { x = (rnd(i, 3) * d.aw + Math.sin(t * 1.3 + i) * 6) % d.aw; y = d.ah - ((t * 18) % (d.ah + 20)); col = i % 3 ? '#ff9a3c' : '#ffd36a'; g.globalAlpha = 0.75; }
    else if (kind === 'snow') { x = (rnd(i, 3) * d.aw + Math.sin(t + i) * 8 + t * 4) % d.aw; y = (t * 14 + rnd(i, 4) * d.ah) % d.ah; col = '#f4f8ff'; s = i % 4 ? 1 : 2; g.globalAlpha = 0.8; }
    else if (kind === 'petal') { x = (rnd(i, 3) * d.aw + t * 9) % d.aw; y = (t * 10 + rnd(i, 4) * d.ah + Math.sin(t * 2 + i) * 4) % d.ah; col = i % 2 ? '#f6b6c8' : '#ffd6e0'; s = 2; g.globalAlpha = 0.8; }
    else { x = (rnd(i, 3) * d.aw + Math.sin(t * 0.7 + i) * 10) % d.aw; y = (rnd(i, 4) * d.ah + Math.cos(t * 0.5 + i) * 8) % d.ah; col = '#fff2c4'; g.globalAlpha = 0.25 + 0.25 * Math.sin(t * 2 + i); }
    g.fillStyle = col;
    g.fillRect(Math.round(x) * K, Math.round(y) * K, s * K, s * K);
  }
  g.restore();
}

const shades = new Map<string, HTMLCanvasElement>();
/** Edge shade and a warm pool of light over the fighting ground (on a slow device it is in the ground: drawGround). */
function lighting(g: CanvasRenderingContext2D, W: number, H: number) {
  if (liteMode()) return;
  g.drawImage(shadeFor(W, H), 0, 0);
}
function shadeFor(W: number, H: number): HTMLCanvasElement {
  const k = `${W}x${H}`;
  let c = shades.get(k);
  if (!c) {
    c = document.createElement('canvas'); c.width = W; c.height = H;
    const x = c.getContext('2d')!;
    const v = x.createRadialGradient(W / 2, H * 0.6, Math.min(W, H) * 0.25, W / 2, H * 0.55, Math.max(W, H) * 0.75);
    v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(8,4,2,.62)');
    x.fillStyle = v; x.fillRect(0, 0, W, H);
    shades.clear(); shades.set(k, c);
  }
  return c;
}

/**
 * On a slow device: the ground scaled up with the lighting already on it, made once per ground and size. That is one
 * full-canvas draw a frame instead of two (the shade then sits under the creatures, not over them).
 */
let litGround: { src: HTMLCanvasElement; W: number; H: number; c: HTMLCanvasElement } | null = null;
function groundLit(ground: HTMLCanvasElement, aw: number, ah: number, W: number, H: number): HTMLCanvasElement {
  if (litGround && litGround.src === ground && litGround.W === W && litGround.H === H) return litGround.c;
  const c = litGround?.W === W && litGround.H === H ? litGround.c : document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d')!;
  x.imageSmoothingEnabled = false;
  x.drawImage(ground, 0, 0, aw, ah, 0, 0, W, H);
  x.drawImage(shadeFor(W, H), 0, 0);
  litGround = { src: ground, W, H, c };
  return c;
}

/** The ground, scaled up with no smoothing. hot: a cell to light while dragging. */
function drawGround(g: CanvasRenderingContext2D, d: Dims, scene: string | undefined, hot?: { row: BoardRow; lane: number; ok: boolean } | null, mineOnly = false) {
  const W = g.canvas.width, H = g.canvas.height;
  // A boss's half has no cells: it is the boss's ground.
  const cells = mineOnly ? sceneCells(d).filter((c) => c.mine) : sceneCells(d);
  const lair = lairGround(scene, d.aw, d.ah, cells, d.aw / 2);
  const ground = lair === undefined ? arenaGround(biomeOf(scene), d.aw, d.ah, cells, d.aw / 2) : lair;
  if (!ground) { g.fillStyle = '#1e140e'; g.fillRect(0, 0, W, H); }
  else if (liteMode()) g.drawImage(groundLit(ground, d.aw, d.ah, W, H), 0, 0);
  else g.drawImage(ground, 0, 0, d.aw, d.ah, 0, 0, W, H);
  if (hot) {
    const p = dev(d, COL_X.left[hot.row], hot.lane);
    g.save();
    g.fillStyle = hot.ok ? 'rgba(244,206,120,.35)' : 'rgba(234,90,69,.35)';
    g.beginPath(); g.ellipse(p.x, p.y, 17 * d.K, 7 * d.K, 0, 0, Math.PI * 2); g.fill();
    g.restore();
  }
}

// ---------------------------------------------------------------- the creatures' cards

const spName = (sp: string) => SPECIES[sp]?.name ?? sp;
const kitCache = new Map<string, MoveDef[]>();
function kitOf(species: string, level: number): MoveDef[] {
  const k = `${species}:${level}`;
  let v = kitCache.get(k);
  if (!v) { try { v = kitMoves({ speciesId: species, level }); } catch { v = []; } kitCache.set(k, v); }
  return v;
}
const hasCharge = (species: string, level: number) => kitOf(species, level).some((m) => m.slot === 'signature' || m.slot === 'awakened');
const SLOT_NAME: Record<string, string> = { basic: 'Basic', light: 'Light', heavy: 'Heavy', coverage: 'Coverage', wave: 'Wave', signature: 'Signature', awakened: 'Awakened' };

function movesHtml(species: string, level: number): string {
  return `<div class="bv-moves">${kitOf(species, level).map((m) => `<span class="bv-mv${m.slot === 'signature' || m.slot === 'awakened' ? ' sig' : ''}" style="--mc:${elColor(m.element)}" title="${esc(SLOT_NAME[m.slot] ?? m.slot)}">${esc(m.name)}</span>`).join('')}</div>`;
}
function elemsHtml(species: string): string {
  return (SPECIES[species]?.elements ?? []).map((e) => `<span class="bv-el" style="--mc:${elColor(e)}">${esc(ELEMENTS[e].name)}</span>`).join('');
}

// ---------------------------------------------------------------- fight replay

/** Replay ms before the fight's first moment (the face-off, the walk-in, "Fight") and after its last (the result). */
const INTRO = 2000, OUTRO = 2000;
const FLOAT_MS = 850, HIT_MS = 120, SWING_LEAD = 280, SIG_LEAD = 480, SIG_MS = 1500;
/** Walking speed, board width per engine ms; the distance a melee creature stands from whoever it hits. */
const WALK_V = 0.0005, ENGAGE = 0.075;
/** How much bigger a boss stands than a creature. */
const BOSS_SCALE = 1.9;
const SPEEDS = [1, 2, 4];

interface Pt { x: number; l: number }
interface Seg { t0: number; t1: number; from: Pt; to: Pt }
interface UnitG {
  s: 0 | 1; i: number; species: string; aura: boolean; level: number; arenaLevel: number; maxHp: number; row: BoardRow; reach: Reach;
  left: boolean; home: Pt; segs: Seg[]; ko: number; charge: boolean; seed: number;
  /** Drawn this much bigger: a boss. */
  scale: number;
}
interface Act { ms: number; s: 0 | 1; a: number; d: number[]; mv?: MoveDef; kind: 'swing' | 'shot' | 'wave' | 'sig'; crit: boolean }
interface Geo { units: UnitG[][]; acts: Act[]; sigs: number[] }

interface Play {
  key: string; r: BattleResult; mine: 0 | 1; labels: [string, string]; stage: BoardStage; opts: StageOpts;
  t: number; playing: boolean; seen: number; html: number; geo: Geo; sel: string | null;
  /** The end has been reached once (played through or skipped): what the fight decides may show from now on. */
  over?: boolean;
}

function posAt(u: UnitG, ms: number): Pt {
  let p = u.home;
  for (const s of u.segs) {
    if (ms >= s.t1) { p = s.to; continue; }
    if (ms <= s.t0) return s.from;
    const k = (ms - s.t0) / Math.max(1, s.t1 - s.t0);
    const e = k * k * (3 - 2 * k);
    return { x: s.from.x + (s.to.x - s.from.x) * e, l: s.from.l + (s.to.l - s.from.l) * e };
  }
  return p;
}
const moving = (u: UnitG, ms: number) => u.segs.some((s) => ms > s.t0 && ms < s.t1);

function buildGeo(r: BattleResult, mine: 0 | 1): Geo {
  const units: UnitG[][] = r.sides.map((side, s) => {
    const left = s === mine;
    const rows = side.team.map((c) => (c.row ?? 'front') as BoardRow);
    const lanes = resolveLanes(side.team.map((c, i) => ({ id: c.id, row: rows[i] })), true);
    // A boss stands alone in the middle of its half, drawn big.
    const isBoss = !!r.boss && s === 1;
    return side.team.map((c, i) => ({
      s: s as 0 | 1, i, species: c.species, aura: c.aura, level: c.level, arenaLevel: c.arenaLevel, maxHp: c.maxHp, row: rows[i],
      reach: c.reach ?? reachOf(c.species), left,
      home: isBoss ? { x: left ? 0.26 : 0.75, l: 2 } : { x: left ? COL_X.left[rows[i]] : COL_X.right[rows[i]], l: lanes[i] }, segs: [], ko: Infinity,
      charge: !r.boss && hasCharge(c.species, c.arenaLevel), seed: (s * 7 + i) * 337, scale: isBoss ? BOSS_SCALE : 1,
    }));
  });
  for (const b of r.log) {
    const d = units[1 - b.s][b.d];
    if (b.hp <= 0 && d.ko === Infinity) d.ko = b.ms;
    if (b.ah !== undefined && b.ah <= 0 && units[b.s][b.a].ko === Infinity) units[b.s][b.a].ko = b.ms;
  }
  // Actions: blows by one creature at one moment.
  const acts: Act[] = [];
  const byKey = new Map<string, Act>();
  for (const b of r.log) {
    const k = `${b.ms}:${b.s}:${b.a}`;
    let a = byKey.get(k);
    const mv = b.mv ? MOVE[b.mv] : undefined;
    if (!a || (mv && a.mv && mv.id !== a.mv.id)) {
      const u = units[b.s][b.a];
      const kind: Act['kind'] = mv && (mv.slot === 'signature' || mv.slot === 'awakened') ? 'sig' : mv?.slot === 'wave' ? 'wave' : u.reach === 'melee' ? 'swing' : 'shot';
      a = { ms: b.ms, s: b.s, a: b.a, d: [], mv, kind, crit: false };
      byKey.set(k, a);
      acts.push(a);
    }
    a.d.push(b.d);
    a.crit ||= b.crit;
  }
  // Melee paths: before each swing, walk up beside the target; each keeps its own side of it so a pack spreads round.
  const lastEnd = new Map<UnitG, number>();
  // A ring round the target: straight in, then above and below, then over its head and under its feet.
  const SLOTS: [number, number][] = [[1, 0], [0.7, -1], [0.7, 1], [0.1, -1.5], [0.1, 1.5]];
  const slot = new Map<UnitG, number>();
  for (const side of units) side.filter((u) => u.reach === 'melee').sort((a, b) => Math.abs(a.home.l - 2) - Math.abs(b.home.l - 2) || a.home.l - b.home.l).forEach((u, k) => slot.set(u, k % SLOTS.length));
  for (const a of acts) {
    if (a.kind !== 'swing') continue;
    const u = units[a.s][a.a];
    // A boss holds its ground; it lunges where it stands.
    if (u.scale > 1) continue;
    const tu = units[1 - a.s][a.d[0]];
    const tp = posAt(tu, a.ms);
    const [fx, dl] = SLOTS[slot.get(u) ?? 0];
    // Off the board's edge, the slot mirrors. A big target gets a wider ring.
    const l = tp.l + dl * tu.scale ** 0.5 < -0.6 || tp.l + dl * tu.scale ** 0.5 > LANES - 0.4 ? tp.l - dl * tu.scale ** 0.5 : tp.l + dl * tu.scale ** 0.5;
    const to: Pt = { x: tp.x + (u.left ? -1 : 1) * ENGAGE * (fx * tu.scale + (tu.scale > 1 ? 0.4 : 0)), l };
    const from = posAt(u, a.ms);
    const dist = Math.hypot(to.x - from.x, (to.l - from.l) * 0.11);
    const prev = lastEnd.get(u) ?? -INTRO * 0.45;
    // It never walks off before its last blow has landed.
    lastEnd.set(u, Math.max(prev, a.ms));
    if (dist < 0.012) continue;
    let arrive = a.ms - SWING_LEAD * 0.6;
    const depart = Math.max(prev, arrive - dist / WALK_V);
    arrive = Math.min(a.ms, Math.max(arrive, depart + Math.min(dist / WALK_V, 160)));
    u.segs.push({ t0: depart, t1: arrive, from, to });
    lastEnd.set(u, Math.max(arrive, a.ms));
  }
  return { units, acts, sigs: acts.filter((a) => a.kind === 'sig').map((a) => a.ms) };
}

/** HP, charge, damage dealt and taken per creature after every blow up to engine time e. */
function stateAt(r: BattleResult, e: number) {
  const hp = r.sides.map((s) => s.team.map((c) => c.maxHp));
  const ch = r.sides.map((s) => s.team.map(() => 0));
  const dealt = r.sides.map((s) => s.team.map(() => 0));
  const taken = r.sides.map((s) => s.team.map(() => 0));
  for (const b of r.log) {
    if (b.ms > e) break;
    const d = 1 - b.s;
    hp[d][b.d] = b.hp;
    if (b.ah !== undefined) hp[b.s][b.a] = b.ah;
    if (b.ca !== undefined) ch[b.s][b.a] = b.ca;
    if (b.cd !== undefined) ch[d][b.d] = b.cd;
    dealt[b.s][b.a] += b.dmg;
    taken[d][b.d] += b.dmg;
  }
  return { hp, ch, dealt, taken };
}

const plays = new Map<string, Play>();
let speed = (() => { try { const v = Number(localStorage.getItem('mi-bv-speed')); return SPEEDS.includes(v) ? v : 1; } catch { return 1; } })();
let meterOn = (() => { try { return localStorage.getItem('mi-bv-meter') !== '0'; } catch { return true; } })();
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** A key for a result from what it holds, so the same fight keeps its replay clock across re-renders. */
function keyOf(r: BattleResult): string {
  let h = 2166136261;
  const s = `${r.sides[0].name}|${r.sides[1].name}|${r.ms}|${r.log.length}|${r.sides.map((x) => x.team.map((c) => c.id).join(',')).join(';')}|${r.log.slice(0, 24).map((b) => b.dmg).join(',')}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36);
}
const endOf = (p: Play) => INTRO + p.r.ms + OUTRO;

function faceTo(dx: number, dy: number, left: boolean): Face {
  if (Math.abs(dy) > Math.abs(dx) * 1.6) return dy > 0 ? 'front' : 'back';
  if (Math.abs(dx) < 1e-6) return left ? 'right' : 'left';
  return dx > 0 ? 'right' : 'left';
}

/** Where each trainer stands: behind their back column, mid-field (wide screens only). */
const trainerAt = (d: Dims, left: boolean) => dev(d, left ? 0.045 : 0.955, 2.1);

/** The top bar: each side's creatures with their HP, the clock in the middle. */
function hud(g: CanvasRenderingContext2D, d: Dims, p: Play, st: ReturnType<typeof stateAt>, e: number, ghost: ReturnType<typeof stateAt>) {
  const K = d.K, W = g.canvas.width;
  const h = 22 * K;
  g.save();
  const band = g.createLinearGradient(0, 0, 0, h + 8 * K);
  band.addColorStop(0, 'rgba(12,7,4,.88)'); band.addColorStop(1, 'rgba(12,7,4,0)');
  g.fillStyle = band; g.fillRect(0, 0, W, h + 8 * K);
  g.fillStyle = 'rgba(244,200,106,.55)'; g.fillRect(0, h, W, Math.max(1, Math.round(K / 2)));
  const sides: (0 | 1)[] = [p.mine, (1 - p.mine) as 0 | 1];
  const slotW = Math.min(22, (d.aw / 2 - 30) / 5) * K;
  sides.forEach((s, k) => {
    const team = p.r.sides[s].team;
    const mine = k === 0;
    if (p.r.boss && s === 1) { bossBar(g, d, p, st, ghost, mine); return; }
    team.forEach((c, i) => {
      const x = mine ? 6 * K + i * slotW : W - 6 * K - (i + 1) * slotW;
      const alive = st.hp[s][i] > 0;
      const img = sprite(c.species, c.aura);
      g.globalAlpha = alive ? 1 : 0.35;
      g.fillStyle = 'rgba(40,26,18,.9)'; g.fillRect(Math.round(x), 3 * K, slotW - 2 * K, 14 * K);
      if (ready(img)) g.drawImage(img, Math.round(x + K), Math.round(3 * K), Math.round(slotW - 4 * K), Math.round(slotW - 4 * K) > 14 * K ? 14 * K : Math.round(slotW - 4 * K));
      const f = Math.max(0, st.hp[s][i] / c.maxHp);
      g.fillStyle = '#2a1c14'; g.fillRect(Math.round(x), 18 * K, slotW - 2 * K, 2 * K);
      g.fillStyle = mine ? C.hpMine : C.hpTheirs; g.fillRect(Math.round(x), 18 * K, Math.round((slotW - 2 * K) * f), 2 * K);
      if (p.sel === `${s}:${i}`) { g.strokeStyle = C.gold; g.lineWidth = K; g.strokeRect(Math.round(x) - K / 2, 2.5 * K, slotW - K, 18 * K); }
    });
  });
  g.globalAlpha = 1;
  const secs = Math.max(0, Math.min(e, p.r.ms)) / 1000;
  const ot = e > BOARD.overtimeMs;
  text(g, secs < 10 ? secs.toFixed(1) : String(Math.floor(secs)), W / 2, 10 * K, 11 * K, ot ? C.bad : C.ink, { font: SANS, weight: 700, line: 3 * K / 2 });
  g.restore();
}

/**
 * A boss's side of the top bar: its name and level over one wide HP bar, the last hits fading out of it. A World
 * Boss (opts.counter) can't fall: the damage dealt so far instead.
 */
function bossBar(g: CanvasRenderingContext2D, d: Dims, p: Play, st: ReturnType<typeof stateAt>, ghost: ReturnType<typeof stateAt>, mine: boolean) {
  const K = d.K, W = g.canvas.width;
  const c = p.r.sides[1].team[0];
  if (!c) return;
  const x0 = mine ? 6 * K : W / 2 + 22 * K, x1 = mine ? W / 2 - 22 * K : W - 6 * K;
  const w = x1 - x0;
  const name = `${spName(c.species)} · Lv ${full(c.level)}`;
  if (p.opts.counter) {
    const dealt = st.dealt[0].reduce((a, x) => a + x, 0);
    text(g, name, x0, 8 * K, 8 * K, C.ink, { font: FONT, align: 'left', line: 2 * K });
    text(g, big(dealt), x1, 11 * K, 12 * K, C.gold, { align: 'right', line: 3 * K });
    return;
  }
  text(g, name, x0, 7 * K, 8 * K, C.ink, { font: FONT, align: 'left', line: 2 * K });
  const hp = Math.max(0, st.hp[1][0]), was = Math.max(hp, ghost.hp[1][0]);
  const Y = 13 * K, h = 6 * K;
  g.fillStyle = 'rgba(12,7,4,.9)'; g.fillRect(Math.round(x0 - K), Math.round(Y - K), Math.round(w + 2 * K), Math.round(h + 2 * K));
  g.fillStyle = '#3a2418'; g.fillRect(Math.round(x0), Math.round(Y), Math.round(w), Math.round(h));
  g.fillStyle = '#f3dca2'; g.fillRect(Math.round(x0), Math.round(Y), Math.round(w * (was / c.maxHp)), Math.round(h));
  const grd = g.createLinearGradient(0, Y, 0, Y + h);
  grd.addColorStop(0, '#f2735a'); grd.addColorStop(1, '#b83324');
  g.fillStyle = grd; g.fillRect(Math.round(x0), Math.round(Y), Math.round(w * (hp / c.maxHp)), Math.round(h));
  // Quarter marks.
  g.fillStyle = 'rgba(12,7,4,.55)';
  for (const q of [0.25, 0.5, 0.75]) g.fillRect(Math.round(x0 + w * q), Math.round(Y), Math.max(1, Math.round(K / 2)), Math.round(h));
  text(g, `${Math.ceil((hp / c.maxHp) * 100)}%`, x1, 7 * K, 7 * K, C.ink, { align: 'right', line: 2 * K });
}

/** Draw one fight frame at replay time p.t. */
function drawFight(cv: HTMLCanvasElement, p: Play) {
  const g = cv.getContext('2d');
  if (!g) return;
  const d = dimsOf(cv), K = d.K, W = cv.width, H = cv.height;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.imageSmoothingEnabled = false;
  const { r, geo } = p;
  const e = p.t - INTRO;
  const st = stateAt(r, e), ghost = stateAt(r, e - 400);
  const real = now();
  const biome = biomeOf(p.opts.scene ?? p.stage);

  // Ground shake on a charge move's landing, lightly on a crit.
  let shake = 0, zoom = 1, zx = W / 2, zy = H / 2;
  for (const a of geo.acts) {
    const age = e - a.ms;
    if (age < -200) break;
    if (a.kind === 'sig' && age >= 0 && age < 320) {
      shake = Math.max(shake, 4 * K * (1 - age / 320));
      const t = geo.units[1 - a.s][a.d[0]];
      const tp = dev(d, posAt(t, e).x, posAt(t, e).l);
      zoom = 1 + 0.05 * (1 - age / 320); zx = tp.x; zy = tp.y;
    } else if (a.crit && age >= 0 && age < 140) shake = Math.max(shake, 1.5 * K * (1 - age / 140));
  }
  g.save();
  if (zoom !== 1) { g.translate(zx, zy); g.scale(zoom, zoom); g.translate(-zx, -zy); }
  if (shake) g.translate(Math.round(Math.sin(real * 0.11) * shake), Math.round(Math.cos(real * 0.13) * shake * 0.6));
  drawGround(g, d, p.opts.scene ?? p.stage, null, !!r.boss);

  // What each creature is doing now.
  const swing = new Map<UnitG, { a: Act; age: number }>();
  for (const a of geo.acts) {
    const lead = a.kind === 'sig' ? SIG_LEAD : a.kind === 'swing' ? SWING_LEAD : 330;
    const age = e - (a.ms - lead);
    if (age < 0) break;
    if (age < lead + 220) swing.set(geo.units[a.s][a.a], { a, age });
  }
  const posNow = (u: UnitG): Pt => {
    let pt = posAt(u, e);
    const sw = swing.get(u);
    if (sw && sw.a.kind === 'swing') {
      const k = (e - sw.a.ms) / 150;
      const lunge = k > -1 && k < 1 ? 1 - Math.abs(k) : 0;
      const tp = posAt(geo.units[1 - u.s][sw.a.d[0]], e);
      const reach = u.scale > 1 ? 0.12 : 0.3;
      pt = { x: pt.x + (tp.x - pt.x) * reach * lunge, l: pt.l + (tp.l - pt.l) * reach * lunge };
    }
    return pt;
  };

  const done = e >= r.ms;
  const intro = e < 0 ? Math.min(1, -e / INTRO) : 0;
  // Trainers behind their teams.
  if (!d.narrow) for (const s of [p.mine, 1 - p.mine] as (0 | 1)[]) {
    const id = p.opts.trainers?.[s];
    if (!id) continue;
    const left = s === p.mine;
    const at = trainerAt(d, left);
    const won = done && r.winner === s;
    const hop = won ? Math.max(0, Math.sin((real + (left ? 0 : 300)) / 160)) * 5 * K : 0;
    drawUnit(g, id, false, at.x, at.y, K, left ? 'right' : 'left', 'idle', real, left ? 0 : 400, { lift: hop });
  }

  // Creatures, far lanes first.
  const all = geo.units.flat().map((u) => ({ u, pt: posNow(u) })).sort((a, b) => a.pt.l - b.pt.l || a.pt.x - b.pt.x);
  const heads = new Map<UnitG, { x: number; y: number; foot: number }>();
  for (const { u, pt } of all) {
    const sp = dev(d, pt.x, pt.l);
    const koAge = e - u.ko;
    heads.set(u, { x: sp.x, y: sp.y - 44 * K * u.scale, foot: sp.y });
    if (koAge > 700) continue;
    let lastHit = -1e9, hitFrom = 0;
    for (const b of r.log) { if (b.ms > e) break; if (1 - b.s === u.s && b.d === u.i) { lastHit = b.ms; hitFrom = posAt(geo.units[b.s][b.a], b.ms).x; } }
    const hitAge = e - lastHit;
    const knock = hitAge >= 0 && hitAge < 150 ? Math.round((1 - hitAge / 150) * 3) * K * (hitFrom < pt.x ? 1 : -1) : 0;
    const sw = swing.get(u);
    let anim: 'idle' | 'walk' | 'atk' = 'idle', ams = real, face: Face = u.left ? 'right' : 'left';
    if (sw && sw.age < 6 * ATK_MS + 20) {
      anim = 'atk'; ams = sw.age;
      const tp = dev(d, posAt(geo.units[1 - u.s][sw.a.d[0]], e).x, posAt(geo.units[1 - u.s][sw.a.d[0]], e).l);
      face = faceTo(tp.x - sp.x, tp.y - sp.y, u.left);
    } else if (moving(u, e)) {
      anim = 'walk'; ams = e;
      const ahead = posAt(u, e + 60);
      const ap = dev(d, ahead.x, ahead.l);
      face = faceTo(ap.x - sp.x, ap.y - sp.y, u.left);
    } else if (u.reach === 'melee' && u.segs.length) {
      const lastSeg = u.segs.filter((s) => s.t1 <= e).pop();
      if (lastSeg) face = u.left ? (lastSeg.to.x >= u.home.x ? 'right' : 'left') : (lastSeg.to.x <= u.home.x ? 'left' : 'right');
    }
    const won = done && r.winner === u.s && u.ko === Infinity;
    const hop = won ? Math.max(0, Math.sin((real + u.seed) / 150)) * 6 * K : 0;
    const sig = sw?.a.kind === 'sig' && sw.age < SIG_LEAD + 80;
    if (sig) {
      // Gathering power: rings of its element under it.
      const el = sw!.a.mv?.element;
      g.save(); g.strokeStyle = elColor(el); g.lineWidth = K;
      for (let k = 0; k < 2; k++) {
        const rr = (10 + ((sw!.age / 5 + k * 9) % 18)) * K * u.scale;
        g.globalAlpha = 0.7 * (1 - ((sw!.age / 5 + k * 9) % 18) / 18);
        g.beginPath(); g.ellipse(sp.x, sp.y, rr * 1.4, rr * 0.5, 0, 0, Math.PI * 2); g.stroke();
      }
      g.restore();
    }
    if (p.sel === `${u.s}:${u.i}`) {
      g.save(); g.strokeStyle = C.gold; g.lineWidth = K;
      g.beginPath(); g.ellipse(sp.x, sp.y, 16 * K * u.scale, 6 * K * u.scale, 0, 0, Math.PI * 2); g.stroke(); g.restore();
    }
    const alpha = koAge >= 0 ? Math.max(0, 1 - koAge / 700) : 1;
    drawUnit(g, u.species, u.aura, sp.x + knock, sp.y + (koAge >= 0 ? (koAge / 700) * 4 * K : 0), K, face, anim, ams, u.seed, {
      // A boss is hit all the time: a fainter flash, so it doesn't stay white.
      alpha, flash: hitAge >= 0 && hitAge < HIT_MS ? (1 - hitAge / HIT_MS) * (u.scale > 1 ? 0.3 : 1) : 0, lift: hop, scale: u.scale,
      tint: koAge >= 0 ? 'rgba(30,20,20,.6)' : sig ? `${elColor(sw!.a.mv?.element)}66` : undefined,
    });
    // A boss's HP is the wide bar across the top instead.
    if (u.scale === 1 && koAge < 250 && e > -INTRO * 0.5) bars(g, sp.x + knock, sp.y - 50 * K - hop, K, st.hp[u.s][u.i], Math.max(st.hp[u.s][u.i], ghost.hp[u.s][u.i]), u.maxHp, u.s === p.mine, u.charge ? st.ch[u.s][u.i] : null, koAge >= 0 ? 1 - koAge / 250 : 1);
    if (koAge >= 0 && koAge < 700) puff(g, 'shade', sp.x, sp.y - 16 * K, koAge, 700, 12, 26 * K, u.seed, K);
  }

  // Move effects, the hunt's own (client/movefx.ts), landing as the blow lands.
  fxBegin(g, K, 1);
  let banner: { a: Act; age: number } | null = null;
  for (const a of geo.acts) {
    if (a.ms - e > 1000) break;
    const u = geo.units[a.s][a.a];
    const mv = a.mv;
    const el = (mv?.element ?? SPECIES[u.species]?.elements?.[0] ?? 'light') as Element;
    const kind = mv?.anim.kind ?? (u.reach === 'melee' ? 'jab' : 'projectile');
    const awk = mv?.slot === 'awakened';
    const t0 = a.ms - fxLand(kind);
    const age = e - t0;
    if (age >= 0 && age <= fxDuration(kind, awk)) {
      const from = heads.get(u)!;
      for (const di of kind === 'wave' || a.d.length === 1 ? a.d : a.d.slice(0, 1)) {
        const t = heads.get(geo.units[1 - a.s][di])!;
        drawMove({ kind, el, x: t.x, y: t.foot - 12 * K, fx: from.x, fy: from.foot - 12 * K, seed: a.ms + di * 31 + a.a, awk, reach: kind === 'wave' ? 26 : undefined }, age, W, H);
      }
    }
    if (a.kind === 'sig' && e - a.ms > -SIG_LEAD && e - a.ms < SIG_MS) banner = { a, age: e - a.ms };
  }
  g.globalAlpha = 1;

  // Floating numbers, on top of every effect.
  for (const b of r.log) {
    const age = e - b.ms;
    if (age < 0) break;
    if (age > FLOAT_MS) continue;
    const h = heads.get(geo.units[1 - b.s][b.d]);
    if (!h) continue;
    const k = age / FLOAT_MS;
    const pop = age < 100 ? 1 + (1 - age / 100) * 0.6 : 1;
    const size = (b.crit ? 12 : 8) * K;
    const col = b.crit ? C.gold : b.mult > 1.001 ? '#ffae6a' : b.mult < 0.999 ? '#c6bcae' : C.ink;
    const jx = (rnd(b.ms, b.d + b.a * 5) - 0.5) * 26 * K;
    text(g, `${big(b.dmg)}${b.crit ? '!' : ''}`, h.x + jx, h.y - k * 28 * K, size, col, { alpha: k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3, line: 2.5 * K, scale: pop });
    if (b.sa) text(g, big(b.sa), h.x + jx + 14 * K, h.y - k * 28 * K + 8 * K, 7 * K, '#a8d8ff', { alpha: 1 - k, line: 2 * K });
  }
  weather(g, d, biome, real);
  g.restore();
  lighting(g, W, H);

  // The charge move's name across the top.
  if (banner) {
    const { a, age } = banner;
    const m = a.mv!;
    const who = spName(geo.units[a.s][a.a].species);
    const label = m.name.includes(who) ? m.name : `${who} · ${m.name}`;
    const al = Math.max(0, age < -SIG_LEAD + 150 ? (age + SIG_LEAD) / 150 : age > SIG_MS - 300 ? (SIG_MS - age) / 300 : 1);
    const y = 40 * K;
    const slide = Math.min(1, (age + SIG_LEAD) / 220);
    g.save();
    g.globalAlpha = al * 0.9;
    const grd = g.createLinearGradient(0, 0, W, 0);
    const c = m.slot === 'awakened' ? '#a8761c' : elColor(m.element);
    grd.addColorStop(0, 'rgba(0,0,0,0)'); grd.addColorStop(0.25, c); grd.addColorStop(0.75, c); grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd; g.fillRect(0, y - 10 * K, W, 20 * K);
    g.fillStyle = 'rgba(0,0,0,.35)'; g.fillRect(0, y + 9 * K, W, K);
    g.restore();
    text(g, label, W / 2 + (1 - slide) * (a.s === p.mine ? -60 : 60) * K, y, 15 * K, m.slot === 'awakened' ? '#fff0b8' : C.ink, { font: FONT, alpha: al, line: 3 * K });
  }
  if (e >= -INTRO * 0.5) hud(g, d, p, st, e, ghost);
  // The face-off: both names slide in round a "VS", then "Fight".
  if (intro > 0.5) {
    const k = (intro - 0.5) / 0.5; // 1 at the start, 0 when it clears
    const inK = Math.min(1, (1 - k) * 3.5);
    const out = k < 0.25 ? k / 0.25 : 1;
    g.save();
    g.globalAlpha = 0.6 * out; g.fillStyle = '#0a0604'; g.fillRect(0, 0, W, H);
    g.globalAlpha = out;
    const cy = H * 0.45;
    const plate = (s: 0 | 1, left: boolean) => {
      const x = left ? W * 0.27 - (1 - inK) * W * 0.6 : W * 0.73 + (1 - inK) * W * 0.6;
      const grd = g.createLinearGradient(x - W * 0.25, 0, x + W * 0.25, 0);
      const col = left ? 'rgba(70,120,70,.85)' : 'rgba(140,50,40,.85)';
      grd.addColorStop(0, left ? 'rgba(0,0,0,0)' : col); grd.addColorStop(1, left ? col : 'rgba(0,0,0,0)');
      g.fillStyle = grd; g.fillRect(x - W * 0.25, cy - 16 * K, W * 0.5, 32 * K);
      text(g, p.labels[s], x, cy, Math.min(18 * K, W * 0.05), C.ink, { font: FONT, alpha: out, line: 3 * K });
    };
    plate(p.mine, true); plate((1 - p.mine) as 0 | 1, false);
    text(g, 'VS', W / 2, cy, 26 * K, C.gold, { font: FONT, alpha: out * inK, line: 4 * K, scale: 0.8 + 0.2 * inK });
    g.restore();
  }
  if (e > -700 && e < 300) {
    const k = (e + 700) / 1000;
    const s = k < 0.15 ? 0.5 + k * 3.3 : 1;
    text(g, 'Fight!', W / 2, H * 0.45, 30 * K, C.gold, { font: FONT, alpha: k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1, line: 4 * K, scale: s });
  }
  if (e >= r.ms + 250) {
    const k = Math.min(1, (e - r.ms - 250) / 350);
    const youSide = p.labels[p.mine] === 'You';
    const won = r.winner === p.mine;
    const label = p.opts.end ?? (youSide ? (won ? 'Victory' : 'Defeat') : `${p.labels[r.winner]} wins`);
    g.save();
    g.globalAlpha = 0.6 * k;
    const grd = g.createLinearGradient(0, 0, W, 0);
    grd.addColorStop(0, 'rgba(0,0,0,0)'); grd.addColorStop(0.5, '#000'); grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd; g.fillRect(0, H * 0.45 - 22 * K, W, 44 * K);
    g.restore();
    text(g, label, W / 2, H * 0.45, 30 * K, youSide ? (won ? C.gold : C.bad) : C.ink, { font: FONT, alpha: k, line: 4 * K, scale: 0.85 + 0.15 * k });
  }
}

/** The HTML round a fight's canvas: controls, the damage meter, the picked creature's card. */
function fightHtml(p: Play): string {
  const { r, geo } = p;
  const e = p.t - INTRO;
  const st = stateAt(r, e);
  const theirs = (1 - p.mine) as 0 | 1;
  const btn = p.playing ? 'Pause' : p.t >= INTRO + r.ms ? 'Replay' : 'Play';
  const meter = meterOn ? `<div class="bv-meter" data-key="meter">${[p.mine, theirs].map((s) => {
    const rows = r.sides[s].team.map((c, i) => ({ c, i, d: st.dealt[s][i] })).sort((a, b) => b.d - a.d || a.i - b.i);
    const top = Math.max(1, ...st.dealt[0], ...st.dealt[1]);
    return `<div class="bv-mcol ${s === p.mine ? 'me' : 'them'}" data-key="m${s}"><b class="bv-mname">${esc(p.labels[s])}</b>${rows.map((x) => `<button class="bv-mrow${st.hp[s][x.i] > 0 ? '' : ' out'}${p.sel === `${s}:${x.i}` ? ' on' : ''}" data-key="mr${s}${x.i}" data-act="bv-pick" data-id="${p.key}" data-u="${s}:${x.i}"><span class="bv-mpic">${creatureIcon(x.c.species, x.c.aura)}</span><span class="bv-mbar"><i style="width:${((x.d / top) * 100).toFixed(1)}%"></i><b>${big(x.d)}</b></span></button>`).join('')}</div>`;
  }).join('')}</div>` : '';
  let card = '';
  if (p.sel) {
    const [s, i] = p.sel.split(':').map(Number) as [0 | 1, number];
    const u = geo.units[s]?.[i];
    if (u) {
      const hp = st.hp[s][i];
      card = `<div class="bv-card" data-key="card"><span class="bv-cpic${u.aura ? ' aura' : ''}">${creatureIcon(u.species, u.aura)}</span><div class="grow">
        <div class="bv-ch1"><b>${esc(spName(u.species))}</b><span class="muted">Lv ${full(u.level)}${u.arenaLevel !== u.level ? ` (fights at ${full(u.arenaLevel)})` : ''}</span>${elemsHtml(u.species)}<span class="bv-tag">${u.reach === 'melee' ? 'Melee' : 'Ranged'}</span><span class="bv-tag">${u.row === 'front' ? 'Front' : 'Back'}</span></div>
        <div class="bv-stats"><span>${hp > 0 ? `${full(hp)} / ${full(u.maxHp)} HP` : 'Knocked out'}</span>${u.charge ? `<span>Charge ${Math.round(st.ch[s][i])}%</span>` : ''}<span>Dealt ${big(st.dealt[s][i])}</span><span>Taken ${big(st.taken[s][i])}</span></div>
        ${movesHtml(u.species, u.arenaLevel)}</div><button class="bv-x" data-act="bv-pick" data-id="${p.key}" data-u="" aria-label="Close">×</button></div>`;
    }
  }
  const pos = Math.max(0, Math.min(1, p.t / endOf(p)));
  return `<div class="bv-wrap${meterOn ? ' with-meter' : ''}" data-key="wrap"><div class="bv-frame" data-key="frame"><canvas class="bv-cv" data-key="cv" data-bvc="${p.key}"></canvas></div>${meter}</div>
    <div class="bv-bar" data-key="bar"><button class="bv-btn main" data-act="bv-play" data-id="${p.key}">${btn}</button>
      <span class="bv-time"><i style="width:${(pos * 100).toFixed(1)}%"></i></span>
      <button class="bv-btn" data-act="bv-speed" data-id="${p.key}" aria-label="Replay speed">${speed}×</button>
      <button class="bv-btn${meterOn ? ' on' : ''}" data-act="bv-meter" data-id="${p.key}" aria-pressed="${meterOn}">Damage</button>
      <button class="bv-btn" data-act="bv-end" data-id="${p.key}"${p.t >= endOf(p) ? ' disabled' : ''}>Skip</button></div>
    ${card}`;
}

/** Each side's synergy chips under the stage, yours first, named by side. */
export function boardChips(r: BattleResult, mine: 0 | 1, labels: [string, string]): string {
  const rows = [mine, (1 - mine) as 0 | 1].map((s) => { const c = battleSynergyChips(r, s); return c ? `<div class="bv-cs"><b>${esc(labels[s])}</b>${c}</div>` : ''; }).join('');
  return rows ? `<div class="bv-chips">${rows}</div>` : '';
}

/**
 * The replay of a board result, seen from side `mine` (on the left). labels: how to name each side ("You", a trainer
 * name), by side. stage: a ranked league's ground, else plain. opts: the ground and the trainers, by side.
 */
export function boardView(r: BattleResult, mine: 0 | 1, labels: [string, string], stage: BoardStage = 'plain', opts: StageOpts = {}): string {
  const key = keyOf(r);
  let p = plays.get(key);
  if (!p) {
    const still = reduced();
    p = { key, r, mine, labels, stage, opts, t: still ? INTRO + r.ms + OUTRO : 0, playing: !still, seen: now(), html: 0, geo: buildGeo(r, mine), sel: null };
    plays.set(key, p);
  }
  if (p.mine !== mine) p.geo = buildGeo(r, mine);
  p.r = r; p.mine = mine; p.labels = labels; p.stage = stage; p.opts = opts; p.seen = now();
  loop();
  return `<div class="bv" data-bv="${key}" aria-label="Battle">${fightHtml(p)}</div>`;
}

// ---------------------------------------------------------------- setup

/** A creature on the setup board. id: a creature id (yours); theirs may use any id. */
export interface PrepUnit { id: number; species: string; level: number; aura?: boolean; row: BoardRow; held?: HeldItem | null }
export interface PrepOpts extends StageOpts {
  /** One setup board per key (a Trail stop, a line-up, a tournament match). */
  key: string;
  mine: PrepUnit[];
  /** Theirs, when known (a Trail trainer, a tournament opponent's last placement); empty leaves their side open. */
  theirs: PrepUnit[];
  /** [yours, theirs]. */
  labels: [string, string];
  stage?: BoardStage;
  /** Rows after a move, by creature id (every creature of yours). */
  onRows: (rows: Record<number, BoardRow>) => void;
  /** Their side is shown as it last stood (a tournament reveal): faded, with a tag. */
  theirsLast?: boolean;
}
interface Prep {
  o: PrepOpts; seen: number; drawn: number; sel: string | null;
  drag: { id: number; x: number; y: number; sx: number; sy: number; moved: boolean } | null;
  hot: { row: BoardRow; lane: number; ok: boolean } | null;
  flash: { at: number; msg: string } | null;
}
const preps = new Map<string, Prep>();

function prepLayout(o: PrepOpts) {
  const mineLanes = resolveLanes(o.mine, true);
  const theirLanes = resolveLanes(o.theirs.map((u, i) => ({ id: -1 - i, row: u.row })), false);
  return {
    mine: o.mine.map((u, i) => ({ u, left: true, pt: { x: COL_X.left[u.row], l: mineLanes[i] }, lane: mineLanes[i] })),
    theirs: o.theirs.map((u, i) => ({ u, left: false, pt: { x: COL_X.right[u.row], l: theirLanes[i] }, lane: theirLanes[i] })),
  };
}

function drawPrep(cv: HTMLCanvasElement, pr: Prep) {
  const g = cv.getContext('2d');
  if (!g) return;
  const d = dimsOf(cv), K = d.K, W = cv.width, H = cv.height;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.imageSmoothingEnabled = false;
  drawGround(g, d, pr.o.scene ?? pr.o.stage, pr.drag?.moved ? pr.hot : null);
  const L = prepLayout(pr.o);
  const real = now();
  if (!d.narrow) for (const [k, id] of (pr.o.trainers ?? []).entries()) {
    if (!id) continue;
    const at = trainerAt(d, k === 0);
    drawUnit(g, id, false, at.x, at.y, K, k === 0 ? 'right' : 'left', 'idle', real, k * 400);
  }
  const list = [...L.mine.map((x, i) => ({ ...x, key: `0:${i}` })), ...L.theirs.map((x, i) => ({ ...x, key: `1:${i}` }))].sort((a, b) => a.pt.l - b.pt.l);
  for (const x of list) {
    const dragging = pr.drag?.moved && x.left && x.u.id === pr.drag.id;
    const sp = dev(d, x.pt.x, x.pt.l);
    if (pr.sel === x.key) {
      g.save(); g.strokeStyle = C.gold; g.lineWidth = K;
      g.beginPath(); g.ellipse(sp.x, sp.y, 16 * K, 6 * K, 0, 0, Math.PI * 2); g.stroke(); g.restore();
    }
    const faded = !x.left && pr.o.theirsLast;
    drawUnit(g, x.u.species, !!x.u.aura, sp.x, sp.y, K, x.left ? 'right' : 'left', 'idle', real, x.u.id * 131, { alpha: dragging ? 0.3 : faded ? 0.75 : 1 });
  }
  // Levels in a small plate at each creature's feet, over every sprite.
  for (const x of list) {
    const dragging = pr.drag?.moved && x.left && x.u.id === pr.drag.id;
    const sp = dev(d, x.pt.x, x.pt.l);
    text(g, full(x.u.level), sp.x + (x.left ? -15 : 15) * K, sp.y + 2 * K, 6 * K, x.left ? '#ffe7a6' : '#ffc4b4', { weight: 800, align: x.left ? 'right' : 'left', line: 2 * K, alpha: dragging ? 0.3 : 0.95 });
  }
  if (pr.drag?.moved) {
    const u = pr.o.mine.find((m) => m.id === pr.drag!.id);
    const k = cv.width / Math.max(1, cssWidth(cv));
    if (u) drawUnit(g, u.species, !!u.aura, pr.drag.x * k, pr.drag.y * k + 24 * K, K, 'right', 'walk', real, u.id, { lift: 6 * K, scale: 1.1 });
  }
  weather(g, d, biomeOf(pr.o.scene ?? pr.o.stage), real);
  lighting(g, W, H);
  // Column names under your side.
  for (const row of ['back', 'front'] as const) {
    const p = dev(d, COL_X.left[row], LANES - 0.35);
    text(g, row === 'front' ? 'FRONT' : 'BACK', p.x, Math.min(p.y + 6 * K, H - 7 * K), 7 * K, 'rgba(250,224,160,.85)', { weight: 700, line: 2 * K });
  }
  if (pr.o.theirsLast && pr.o.theirs.length) {
    const p = dev(d, 0.76, LANES - 0.35);
    text(g, 'LAST GAME', p.x, Math.min(p.y + 6 * K, H - 7 * K), 8 * K, 'rgba(255,200,180,.95)', { weight: 700, line: 2 * K });
  }
  if (pr.flash && real - pr.flash.at < 1400) {
    const k = (real - pr.flash.at) / 1400;
    text(g, pr.flash.msg, W / 2, H * 0.14, 13 * K, C.bad, { font: FONT, alpha: k < 0.75 ? 1 : 1 - (k - 0.75) / 0.25, line: 3 * K });
  }
}

function prepCard(pr: Prep): string {
  if (!pr.sel) return '';
  const [s, i] = pr.sel.split(':').map(Number);
  const u = (s === 0 ? pr.o.mine : pr.o.theirs)[i];
  if (!u) return '';
  const reach = reachOf(u.species);
  return `<div class="bv-card" data-key="card"><span class="bv-cpic${u.aura ? ' aura' : ''}">${creatureIcon(u.species, !!u.aura)}</span><div class="grow">
    <div class="bv-ch1"><b>${esc(spName(u.species))}</b><span class="muted">Lv ${full(u.level)}</span>${elemsHtml(u.species)}<span class="bv-tag">${reach === 'melee' ? 'Melee' : 'Ranged'}</span><span class="bv-tag">${u.row === 'front' ? 'Front' : 'Back'}</span>${u.held ? `<span class="bv-tag held">${esc(heldName(u.held))}</span>` : ''}</div>
    ${movesHtml(u.species, u.level)}</div><button class="bv-x" data-act="bv-ppick" data-id="${esc(pr.o.key)}" data-u="" aria-label="Close">×</button></div>`;
}

function prepHtml(pr: Prep): string {
  const o = pr.o;
  const fronts = o.mine.filter((u) => u.row === 'front').length;
  return `<div class="bv-head" data-key="head"><span class="bv-who me"><b>${esc(o.labels[0])}</b><span class="bv-front${fronts >= BOARD.frontMax ? ' full' : ''}">Front ${fronts}/${BOARD.frontMax}</span></span>${o.theirs.length ? `<span class="bv-vs">vs</span><span class="bv-who them"><b>${esc(o.labels[1])}</b></span>` : ''}</div>
    <div class="bv-wrap" data-key="wrap"><div class="bv-frame" data-key="frame"><canvas class="bv-cv prep" data-key="cv" data-bvp="${esc(o.key)}"></canvas></div></div>${prepCard(pr)}`;
}

/** The setup board: your team on your cells (drag or tap to move), theirs across, a card for whichever is tapped. */
export function boardPrep(o: PrepOpts): string {
  let pr = preps.get(o.key);
  if (!pr) { pr = { o, seen: now(), drawn: 0, sel: null, drag: null, hot: null, flash: null }; preps.set(o.key, pr); }
  pr.o = o; pr.seen = now();
  loop();
  return `<div class="bv bv-prep" data-bvprep="${esc(o.key)}" aria-label="Board">${prepHtml(pr)}</div>`;
}

/** The cell of yours nearest a canvas point (canvas pixels), if one is close enough. */
function cellAt(d: Dims, x: number, y: number): { row: BoardRow; lane: number } | null {
  let best: { row: BoardRow; lane: number } | null = null, bd = Infinity;
  for (const row of ['back', 'front'] as const) for (let l = 0; l < LANES; l++) {
    const p = dev(d, COL_X.left[row], l);
    const dd = Math.hypot((x - p.x) / (30 * d.K), (y - p.y) / (20 * d.K));
    if (dd < bd) { bd = dd; best = { row, lane: l }; }
  }
  return bd < 1.3 ? best : null;
}

/** The creature under a canvas point: "side:index", nearest first. */
function unitAt(d: Dims, x: number, y: number, pts: { key: string; pt: Pt }[]): string | null {
  let best: string | null = null, bd = Infinity;
  for (const q of pts) {
    const sp = dev(d, q.pt.x, q.pt.l);
    const dx = Math.abs(x - sp.x), dy = sp.y - y;
    if (dx < 18 * d.K && dy > -6 * d.K && dy < 46 * d.K) {
      const dd = dx + Math.abs(dy - 18 * d.K);
      if (dd < bd) { bd = dd; best = q.key; }
    }
  }
  return best;
}

/** Move one of your creatures to a cell: a taken cell swaps, the front holds BOARD.frontMax. */
function placeAt(pr: Prep, id: number, row: BoardRow, lane: number) {
  const o = pr.o;
  const L = prepLayout(o);
  const me = L.mine.find((x) => x.u.id === id);
  if (!me) return;
  const there = L.mine.find((x) => x.u.row === row && x.lane === lane && x.u.id !== id);
  const rows: Record<number, BoardRow> = Object.fromEntries(o.mine.map((u) => [u.id, u.row]));
  if (there) {
    rows[there.u.id] = me.u.row;
    laneStore[String(there.u.id)] = me.lane;
  } else if (row === 'front' && me.u.row !== 'front' && o.mine.filter((u) => u.row === 'front').length >= BOARD.frontMax) {
    pr.flash = { at: now(), msg: `The front holds ${BOARD.frontMax}` };
    return;
  }
  rows[id] = row;
  laneStore[String(id)] = lane;
  // Keep every other creature where it stands now, so the move doesn't reshuffle the rest.
  for (const x of L.mine) if (x.u.id !== id && x.u.id !== there?.u.id) laneStore[String(x.u.id)] = x.lane;
  saveLanes();
  o.onRows(rows);
}

const bound = new WeakSet<HTMLCanvasElement>();
function bindCanvas(cv: HTMLCanvasElement) {
  if (bound.has(cv)) return;
  bound.add(cv);
  /** A pointer in CSS pixels, and in canvas pixels. */
  const local = (ev: PointerEvent) => { const b = cv.getBoundingClientRect(); const x = ev.clientX - b.left, y = ev.clientY - b.top; const k = cv.width / Math.max(1, b.width); return { x, y, cx: x * k, cy: y * k }; };
  cv.addEventListener('pointerdown', (ev) => {
    const pkey = cv.dataset.bvp, fkey = cv.dataset.bvc;
    const { x, y, cx, cy } = local(ev);
    const d = dimsOf(cv);
    if (fkey) {
      const p = plays.get(fkey);
      if (!p) return;
      const e = p.t - INTRO;
      const hit = unitAt(d, cx, cy, p.geo.units.flat().filter((u) => e - u.ko < 700).map((u) => ({ key: `${u.s}:${u.i}`, pt: posAt(u, e) })));
      p.sel = hit && hit !== p.sel ? hit : null;
      redraw(p.key);
      return;
    }
    const pr = pkey ? preps.get(pkey) : undefined;
    if (!pr) return;
    const L = prepLayout(pr.o);
    const hit = unitAt(d, cx, cy, [...L.mine.map((m, i) => ({ key: `0:${i}`, pt: m.pt })), ...L.theirs.map((m, i) => ({ key: `1:${i}`, pt: m.pt }))]);
    if (hit?.startsWith('0:')) {
      const u = pr.o.mine[Number(hit.slice(2))];
      pr.drag = { id: u.id, x, y, sx: x, sy: y, moved: false };
      cv.setPointerCapture(ev.pointerId);
      ev.preventDefault();
      return;
    }
    if (hit) { pr.sel = hit === pr.sel ? null : hit; redrawPrep(pr); return; }
    // An empty cell with one of yours picked: move it there.
    const cell = cellAt(d, cx, cy);
    if (cell && pr.sel?.startsWith('0:')) {
      const u = pr.o.mine[Number(pr.sel.slice(2))];
      if (u) placeAt(pr, u.id, cell.row, cell.lane);
      return;
    }
    pr.sel = null; redrawPrep(pr);
  });
  cv.addEventListener('pointermove', (ev) => {
    const pr = cv.dataset.bvp ? preps.get(cv.dataset.bvp) : undefined;
    if (!pr?.drag) return;
    const { x, y, cx, cy } = local(ev);
    pr.drag.x = x; pr.drag.y = y;
    if (Math.hypot(x - pr.drag.sx, y - pr.drag.sy) > 6) pr.drag.moved = true;
    const cell = pr.drag.moved ? cellAt(dimsOf(cv), cx, cy) : null;
    if (cell) {
      const me = pr.o.mine.find((u) => u.id === pr.drag!.id);
      const L = prepLayout(pr.o);
      const taken = L.mine.some((m) => m.u.row === cell.row && m.lane === cell.lane && m.u.id !== pr.drag!.id);
      const ok = taken || cell.row !== 'front' || me?.row === 'front' || pr.o.mine.filter((u) => u.row === 'front').length < BOARD.frontMax;
      pr.hot = { row: cell.row, lane: cell.lane, ok };
    } else pr.hot = null;
    loop();
  });
  const end = (ev: PointerEvent) => {
    const pr = cv.dataset.bvp ? preps.get(cv.dataset.bvp) : undefined;
    if (!pr?.drag) return;
    const dr = pr.drag;
    pr.drag = null; pr.hot = null;
    if (ev.type === 'pointercancel') { redrawPrep(pr); return; }
    if (!dr.moved) {
      const k = `0:${pr.o.mine.findIndex((u) => u.id === dr.id)}`;
      pr.sel = pr.sel === k ? null : k;
      redrawPrep(pr);
      return;
    }
    const { cx, cy } = local(ev);
    const cell = cellAt(dimsOf(cv), cx, cy);
    if (cell) placeAt(pr, dr.id, cell.row, cell.lane);
    redrawPrep(pr);
  };
  cv.addEventListener('pointerup', end);
  cv.addEventListener('pointercancel', end);
}

// ---------------------------------------------------------------- the clock

/** Fit a canvas's backing store to a whole number of canvas pixels per source pixel, at least its CSS size. */
function fit(cv: HTMLCanvasElement): boolean {
  const cw = cssWidth(cv);
  if (!cw) return false;
  const narrow = cw < 520;
  const aw = narrow ? 300 : 480, ah = narrow ? 360 : 270;
  const dpr = canvasDpr();
  const K = Math.max(1, Math.ceil((cw * dpr) / aw));
  const w = aw * K, h = ah * K;
  if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
  return true;
}

let raf = 0, last = 0;
function loop() { if (!raf && typeof requestAnimationFrame === 'function') { last = 0; raf = requestAnimationFrame(tick); } }

/** Replay time runs slower round a charge move's landing. */
function slowAt(p: Play): number {
  const e = p.t - INTRO;
  return p.geo.sigs.some((ms) => e > ms - SIG_LEAD * 0.8 && e < ms + 300) ? 0.35 : 1;
}

function tick(ts: number) {
  raf = 0;
  const dt = last ? Math.min(100, ts - last) : 16;
  const timed = last > 0;
  last = ts;
  let any = false, played = false;
  for (const [key, p] of plays) {
    const el = document.querySelector(`[data-bv="${key}"]`);
    if (!el) { if (now() - p.seen > 4000) plays.delete(key); continue; }
    p.seen = now();
    if (p.playing) {
      played = true;
      p.t += dt * speed * slowAt(p);
      if (p.t >= endOf(p)) { p.t = endOf(p); p.playing = false; }
    }
    reveal(p);
    // The HTML round it a few times a second, then the canvas.
    if (ts - p.html > (liteMode() ? 300 : 150) || !p.playing) { p.html = ts; patch(el, fightHtml(p)); }
    const cv = el.querySelector<HTMLCanvasElement>('canvas.bv-cv');
    if (cv) { bindCanvas(cv); if (fit(cv)) drawFight(cv, p); }
    any = true;
  }
  for (const [key, pr] of preps) {
    const el = document.querySelector(`[data-bvprep="${CSS.escape(key)}"]`);
    if (!el) { if (now() - pr.seen > 4000) preps.delete(key); continue; }
    pr.seen = now();
    const cv = el.querySelector<HTMLCanvasElement>('canvas.bv-cv');
    if (cv) { bindCanvas(cv); if (ts - pr.drawn >= 30 || pr.drag) { pr.drawn = ts; if (fit(cv)) drawPrep(cv, pr); } }
    any = true;
  }
  if (played && timed) frameTime(dt);
  if (any && !document.hidden) raf = requestAnimationFrame(tick);
  else if (any) setTimeout(loop, 500);
  else last = 0;
}

function redraw(key: string) {
  const p = plays.get(key);
  if (p) reveal(p);
  const el = document.querySelector(`[data-bv="${key}"]`);
  if (p && el) patch(el, fightHtml(p));
  loop();
}
function redrawPrep(pr: Prep) {
  const el = document.querySelector(`[data-bvprep="${CSS.escape(pr.o.key)}"]`);
  if (el) patch(el, prepHtml(pr));
  loop();
}

/** Clicks on data-act="bv-…". True when handled. */
export function boardViewClick(el: HTMLElement): boolean {
  const act = el.dataset.act ?? '';
  if (!act.startsWith('bv-')) return false;
  const key = el.dataset.id ?? '';
  if (act === 'bv-ppick') {
    const pr = preps.get(key);
    if (pr) { pr.sel = el.dataset.u || null; redrawPrep(pr); }
    return true;
  }
  const p = plays.get(key);
  if (!p) return true;
  switch (act) {
    case 'bv-play':
      if (p.playing) p.playing = false;
      else { if (p.t >= INTRO + p.r.ms) p.t = 0; p.playing = true; }
      break;
    case 'bv-speed':
      speed = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
      try { localStorage.setItem('mi-bv-speed', String(speed)); } catch { /* storage off */ }
      break;
    case 'bv-meter':
      meterOn = !meterOn;
      try { localStorage.setItem('mi-bv-meter', meterOn ? '1' : '0'); } catch { /* storage off */ }
      break;
    case 'bv-pick': p.sel = el.dataset.u || null; break;
    case 'bv-end': p.t = endOf(p); p.playing = false; break;
    default: return false;
  }
  redraw(key);
  return true;
}

/** Once the fight is over on screen, show what was held back (afterReplay). */
function reveal(p: Play) {
  if (p.over || p.t < INTRO + p.r.ms) return;
  p.over = true;
  for (const n of document.querySelectorAll<HTMLElement>(`[data-after="${p.key}"]`)) n.hidden = false;
  window.dispatchEvent(new CustomEvent('mi-replay-over'));
}

/** Whether what a fight decides may show: its replay has ended or was skipped once, or it has none on screen. */
export function replayOver(r: BattleResult): boolean {
  const p = plays.get(keyOf(r));
  return !p || !!p.over || p.t >= INTRO + p.r.ms;
}

/**
 * What a fight's outcome gives away (the result line, rewards, "Next"), held back until its replay ends or is
 * skipped, so the ending isn't spoiled at 0.0. Call after boardView for the same result.
 */
export function afterReplay(r: BattleResult | null, html: string, tag: 'div' | 'span' = 'div'): string {
  if (!html || !r) return html;
  return `<${tag} class="bv-after" data-after="${keyOf(r)}"${replayOver(r) ? '' : ' hidden'}>${html}</${tag}>`;
}

/** Start a fight's replay over from the face-off (a fresh result that should play at once). */
export function replayFromStart(r: BattleResult) {
  const p = plays.get(keyOf(r));
  if (p && !reduced()) { p.t = 0; p.playing = true; loop(); }
}

/** For tests: the geometry a fight replays on. */
export const _test = { buildGeo, resolveLanes, posAt, stateAt };
export type { Blow };
